import { useRef, useState, useEffect, useMemo } from "react";
import { CoreActivitiesComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useScrollSpy } from "./useScrollSpy";

export interface Tab {
  id: string;
  label: string;
}

interface UseActivityShopTabsProps {
  onTabClick?: (sectionId: string) => void;
}

export const useActivityShopTabs = ({
  onTabClick,
}: UseActivityShopTabsProps) => {
  const { t } = useI18nContext();
  const isScrollingRef = useRef(false);
  const [activeTab, setActiveTab] = useState<string | undefined>();

  const tabs: Tab[] = useMemo(
    () => [
      {
        id: CoreActivitiesComponent.ActivityOverviewSection,
        label: t("core-activities.shopPage.tabs.overview"),
      },
      {
        id: CoreActivitiesComponent.ActivityInclusionsSection,
        label: t("core-activities.shopPage.tabs.whatsIncluded"),
      },
      {
        id: CoreActivitiesComponent.ActivityMeetupSection,
        label: t("core-activities.shopPage.tabs.meetingAndMeetup"),
      },
      {
        id: CoreActivitiesComponent.ActivityReviewsSection,
        label: t("core-activities.shopPage.tabs.reviews"),
      },
      {
        id: CoreActivitiesComponent.ActivityItinerarySection,
        label: t("core-activities.shopPage.tabs.itinerary"),
      },
      {
        id: CoreActivitiesComponent.ActivityAdditionalInformationSection,
        label: t("core-activities.shopPage.tabs.additionalInfo"),
      },
    ],
    [t]
  );

  const sectionIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const scrollSpyActiveSection = useScrollSpy(sectionIds, { isScrollingRef });

  // Update active tab from scroll spy when not programmatically scrolling
  useEffect(() => {
    if (isScrollingRef.current === false) {
      setActiveTab(scrollSpyActiveSection);
    }
  }, [scrollSpyActiveSection]);

  // Scroll active tab into view horizontally when it changes
  useEffect(() => {
    if (typeof activeTab === "string" && activeTab !== "") {
      const tabsContainer = document.getElementById(
        CoreActivitiesComponent.ActivityShopTabs
      );
      const activeButton = tabsContainer?.querySelector(
        `button[data-tab-id="${activeTab}"]`
      ) as HTMLElement;

      if (activeButton != null) {
        activeButton.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeTab]);

  const handleTabClick = (tabId: string) => {
    // Immediately update the active tab for instant feedback
    setActiveTab(tabId);

    // Prevent scroll spy from interfering with programmatic scrolling
    isScrollingRef.current = true;

    // Scroll to the corresponding section with offset
    const sectionElement = document.getElementById(tabId);
    if (sectionElement == null) {
      // Re-enable scroll spy immediately since no scroll will happen
      isScrollingRef.current = false;
      return;
    }

    const tabsElement = document.getElementById(
      CoreActivitiesComponent.ActivityShopTabs
    );
    const remInPixels = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );

    // Get the sticky top position from CSS
    const tabsStyles = tabsElement ? getComputedStyle(tabsElement) : null;
    const stickyTop = tabsStyles
      ? parseFloat(tabsStyles.top)
      : 15 * remInPixels; // fallback to 15rem

    // Use actual tabs height if available, otherwise estimate ~60px
    const tabsHeight = tabsElement?.offsetHeight ?? 60;

    // Total offset = sticky top position + tabs height + 1rem spacing
    const offset = stickyTop + tabsHeight + remInPixels;

    const elementPosition =
      sectionElement.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });

    // Re-enable scroll spy after scrolling completes
    // Use scrollend event for better accuracy, with setTimeout fallback
    const handleScrollEnd = () => {
      isScrollingRef.current = false;
      window.removeEventListener("scrollend", handleScrollEnd);
    };

    window.addEventListener("scrollend", handleScrollEnd, { once: true });

    // Fallback for browsers without scrollend support
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 1000);

    // Call the optional callback
    if (onTabClick) {
      onTabClick(tabId);
    }
  };

  return {
    activeTab,
    handleTabClick,
    tabs,
  };
};
